import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FigmaChevronDownIcon } from '../icons';
import './SpaceSelect.css';

const optionId = (space) => space?.id || space?.space_id || space?.spaceId || space?.name || '';

const sameId = (a, b) => String(a ?? '') === String(b ?? '');

const SpaceSelect = ({ id, spaces = [], value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const placeMenu = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuStyle({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    placeMenu();
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
    };
    const onPointerDown = (event) => {
      if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', placeMenu, true);
    window.addEventListener('resize', placeMenu);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', placeMenu, true);
      window.removeEventListener('resize', placeMenu);
    };
  }, [open, placeMenu]);

  const selected = spaces.find((space) => sameId(optionId(space), value));

  return (
    <div className="space-select booking-select-wrapper">
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`booking-select booking-select-trigger${open ? ' is-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={spaces.length === 0}
        onClick={() => {
          if (spaces.length === 0) return;
          setOpen((current) => !current);
        }}
      >
        {selected?.name || selected?.space_name || 'No spaces available'}
      </button>
      <FigmaChevronDownIcon className={`booking-select-icon${open ? ' is-open' : ''}`} />
      {open && spaces.length > 0
        ? createPortal(
            <div ref={menuRef} className="vd-select-menu" style={menuStyle} role="listbox">
              {spaces.map((space) => {
                const spaceKey = optionId(space);
                const isSelected = sameId(spaceKey, value);
                return (
                  <button
                    key={spaceKey}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`vd-select-option${isSelected ? ' is-selected' : ''}`}
                    onClick={() => {
                      onChange(spaceKey);
                      setOpen(false);
                    }}
                  >
                    {space.name || space.space_name || 'Space'}
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default SpaceSelect;
